import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, useDialogRequestClose } from "@/components/ui/Dialog";
import { hasFormChanges } from "@/components/ui/dialogCloseGuard";
import { TextInput } from "@/components/ui/Field";
import { Button } from "@/components/ui/Button";
import { LockedAutocomplete } from "@/components/LockedAutocomplete";
import type { AutocompleteOption, LockedAutocompleteHandle } from "@/components/LockedAutocomplete";
import {
  emptyStuecklisteWerkzeugPosition,
  listStuecklisteWerkzeugVorschlaege,
  type StuecklistePosition,
  type StuecklisteWerkzeugVorschlag,
} from "@/lib/stuecklisten";
import { PROJECT_OVERVIEW_NESTED_OVERLAY_Z } from "@/projectOverview/projectOverviewLayers";

interface WerkzeugFormState {
  beschreibung: string;
  anzahl: string;
  bemerkung: string;
}

function positionToForm(pos: StuecklistePosition | null | undefined): WerkzeugFormState {
  if (!pos) {
    return { beschreibung: "", anzahl: "1", bemerkung: "" };
  }
  return {
    beschreibung: pos.beschreibung || "",
    anzahl: pos.anzahl != null ? String(pos.anzahl) : "",
    bemerkung: pos.bemerkung || "",
  };
}

function formToPosition(
  form: WerkzeugFormState,
  existing?: StuecklistePosition | null,
): StuecklistePosition {
  const positionNr = existing?.position_nr || 1;
  const base = existing ?? emptyStuecklisteWerkzeugPosition(positionNr);
  const anzahlRaw = form.anzahl.trim().replace(",", ".");
  const anzahl = anzahlRaw ? Number(anzahlRaw) : null;
  return {
    ...base,
    position_kind: "werkzeug",
    anzahl: anzahl != null && Number.isFinite(anzahl) ? anzahl : null,
    groesse: null,
    beschreibung: form.beschreibung.trim() || null,
    bemerkung: form.bemerkung.trim() || null,
    artikel_id: null,
    hero_nr: null,
    source_type: "manual",
    position_status: "offen",
    order_line_id: null,
    bestellnummer: null,
    zuschnitt_erforderlich: false,
    ist_montagematerial: false,
    schnittwinkel_links: null,
    schnittwinkel_rechts: null,
    schnittlage: null,
  };
}

function vorschlagToOption(
  row: StuecklisteWerkzeugVorschlag,
): AutocompleteOption<StuecklisteWerkzeugVorschlag> {
  return {
    key: row.beschreibung,
    label: row.beschreibung,
    description: row.usage_count > 1 ? `${row.usage_count}× verwendet` : undefined,
    value: row,
  };
}

function freeTextOption(query: string): AutocompleteOption<StuecklisteWerkzeugVorschlag> {
  const label = query.trim();
  return {
    key: `free:${label}`,
    label,
    description: "Neue Bezeichnung",
    value: { beschreibung: label, usage_count: 0 },
  };
}

export function StuecklisteWerkzeugDialog({
  open,
  editPosition,
  onClose,
  onSave,
}: {
  open: boolean;
  editPosition?: StuecklistePosition | null;
  onClose: () => void;
  onSave: (position: StuecklistePosition) => void;
}) {
  const autocompleteRef = useRef<LockedAutocompleteHandle | null>(null);
  const [form, setForm] = useState<WerkzeugFormState>(() => positionToForm(editPosition));
  const [baseline, setBaseline] = useState<WerkzeugFormState>(() => positionToForm(editPosition));
  const [beschreibungOption, setBeschreibungOption] = useState<AutocompleteOption<StuecklisteWerkzeugVorschlag> | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const next = positionToForm(editPosition);
    setForm(next);
    setBaseline(next);
    setBeschreibungOption(next.beschreibung.trim() ? freeTextOption(next.beschreibung) : null);
    setError(null);
  }, [open, editPosition]);

  const dirty = useMemo(() => hasFormChanges(form, baseline), [form, baseline]);

  async function loadOptions(query: string) {
    const rows = await listStuecklisteWerkzeugVorschlaege(query);
    return (Array.isArray(rows) ? rows : []).map(vorschlagToOption);
  }

  function handleSave() {
    const pending = autocompleteRef.current?.getPendingQuery()?.trim() || "";
    const beschreibung =
      beschreibungOption?.label?.trim() || pending || form.beschreibung.trim();
    if (!beschreibung) {
      setError("Bitte eine Bezeichnung angeben.");
      return;
    }
    onSave(formToPosition({ ...form, beschreibung }, editPosition));
    onClose();
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      dirty={dirty}
      title={editPosition ? "Werkzeug bearbeiten" : "Besonderes Werkzeug"}
      zIndexClass={PROJECT_OVERVIEW_NESTED_OVERLAY_Z}
      footer={<WerkzeugDialogFooter onClose={onClose} onSubmit={handleSave} />}
    >
      <div className="space-y-3">
        <LockedAutocomplete<StuecklisteWerkzeugVorschlag>
          ref={autocompleteRef}
          label="Bezeichnung"
          placeholder="z. B. Steiger, Staubschutzvorhang …"
          value={beschreibungOption}
          minChars={0}
          allowFreeText
          allowEditLocked={() => true}
          createFreeTextOption={freeTextOption}
          loadOptions={loadOptions}
          emptyText="Keine bisherigen Bezeichnungen"
          required
          onChange={(opt) => {
            setBeschreibungOption(opt);
            setForm((prev) => ({
              ...prev,
              beschreibung: opt?.label?.trim() || "",
            }));
          }}
        />
        <TextInput
          label="Anzahl"
          value={form.anzahl}
          onChange={(e) => setForm((prev) => ({ ...prev, anzahl: e.target.value }))}
          inputMode="decimal"
        />
        <TextInput
          label="Bemerkung"
          value={form.bemerkung}
          onChange={(e) => setForm((prev) => ({ ...prev, bemerkung: e.target.value }))}
        />
        {error ? <p className="text-sm text-status-red">{error}</p> : null}
      </div>
    </Dialog>
  );
}

function WerkzeugDialogFooter({
  onClose,
  onSubmit,
}: {
  onClose: () => void;
  onSubmit: () => void;
}) {
  const requestClose = useDialogRequestClose(onClose);
  return (
    <div className="flex justify-end gap-2">
      <Button type="button" variant="secondary" onClick={requestClose}>
        Abbrechen
      </Button>
      <Button type="button" onClick={onSubmit}>
        Übernehmen
      </Button>
    </div>
  );
}
